/**
 * App.Routing - 3D Dijkstra + 跨层竖井 + 绑定 + 主流程
 *
 * 节点 = (floorIdx, x, z)。
 * 横向4邻权重1；当前格为RISER且相邻层同位亦为RISER时可竖向跳层，权重=FLOOR_HEIGHT。
 * 求真实最短距离（Dijkstra，小网格线性扫描优先队列）。
 */

import type { Floor, Room, End, Node3D, RouteResult } from '../types';
import { TileType } from '../types';
import { App } from './App';

/* ─────────────── Module: Routing（3D Dijkstra + 跨层竖井 + 绑定 + 主流程）─────────────── */
App.Routing = {
  /** 方向数组：横向4邻 */
  NB: [[1, 0], [-1, 0], [0, 1], [0, -1]],

  // ════════════════════════════════════════════════════════════════════
  // 跨楼层收集
  // ════════════════════════════════════════════════════════════════════

  /**
   * 收集所有机房的数组
   */
  allRooms(): Room[] {
    const a: Room[] = [];
    for (const f of App.Store.floors) {
      for (const r of f.rooms) {
        a.push(r);
      }
    }
    return a;
  },

  /**
   * 收集所有末端的数组
   */
  allEnds(): End[] {
    const a: End[] = [];
    for (const f of App.Store.floors) {
      for (const e of f.ends) {
        a.push(e);
      }
    }
    return a;
  },

  // ════════════════════════════════════════════════════════════════════
  // 3D Dijkstra 寻路
  // ════════════════════════════════════════════════════════════════════

  /**
   * 3D Dijkstra 算法
   * @param starts 起点节点数组（多源）
   * @param goal 终点节点
   * @returns 路径结果
   */
  dijkstra(starts: Node3D[], goal: Node3D, allowSystem?: number): { path: Node3D[] | null; visited: Node3D[] } {
    const S = App.Store;
    const W = App.World;
    const C = App.Config;
    const FH = C.FLOOR_HEIGHT;
    const T = C.T;
    // 折弯惩罚（成本权重可调：最短长度 vs 最少折弯）
    const penalty = S.cornerPenalty || 0;
    // 跨层竖井系统限制：只有 systemId === allowSystem 的竖井才允许跳层（不同专业管线不混用竖井）
    const sysLimit = allowSystem != null;

    if (goal.fi == null || !S.floors[goal.fi] || !W.walkable(goal.x, goal.z, S.floors[goal.fi])) {
      return { path: null, visited: [] };
    }

    // key 含方向 dir：-1=源点，0~3=横向(NB索引)，4=竖向跳层
    const key = (fi: number, x: number, z: number, dir: number) => fi + ':' + x + ',' + z + ':' + dir;
    const parseK = (k: string): [number, number, number] => {
      const [fi, xy] = k.split(':');
      const [x, z] = xy.split(',').map(Number);
      return [+fi, x, z];
    };

    const dist = new Map<string, number>();
    const back = new Map<string, string | null>();
    const order: Node3D[] = [];
    const orderSeen = new Set<string>();
    const pq: Array<{ k: string; d: number; fi: number; x: number; z: number; dir: number }> = [];

    // 初始化优先队列（多源，源点 dir=-1 无折弯）
    for (const s of starts) {
      if (s.fi == null || !S.floors[s.fi]) continue;
      if (!W.walkable(s.x, s.z, S.floors[s.fi])) continue;
      const k = key(s.fi, s.x, s.z, -1);
      if (!dist.has(k)) {
        dist.set(k, 0);
        back.set(k, null);
        pq.push({ k, d: 0, fi: s.fi, x: s.x, z: s.z, dir: -1 });
      }
    }

    if (!pq.length) return { path: null, visited: [] };

    // Dijkstra 主循环
    while (pq.length) {
      // 线性扫描找最小（小网格效率足够）
      let bi = 0;
      for (let i = 1; i < pq.length; i++) {
        if (pq[i].d < pq[bi].d) bi = i;
      }
      const cur = pq.splice(bi, 1)[0];

      // 过期项跳过
      if (cur.d > (dist.get(cur.k) ?? Infinity)) continue;

      // 到达目标（任意方向到达即可）
      if (cur.fi === goal.fi && cur.x === goal.x && cur.z === goal.z) {
        const path: Node3D[] = [];
        let k: string | null = cur.k;
        while (k) {
          const [fi, x, z] = parseK(k);
          path.push({ fi, x, z });
          k = back.get(k)!;
        }
        return { path: path.reverse(), visited: order };
      }

      // 记录访问（按格子去重，用于搜索过程可视化）
      const ok = cur.fi + ',' + cur.x + ',' + cur.z;
      if (!orderSeen.has(ok)) {
        orderSeen.add(ok);
        order.push({ fi: cur.fi, x: cur.x, z: cur.z });
      }

      const cf = S.floors[cur.fi];

      // 横向4邻（权重1 + 方向变化则加折弯惩罚）
      for (let i = 0; i < this.NB.length; i++) {
        const [dx, dz] = this.NB[i];
        const nx = cur.x + dx, nz = cur.z + dz;
        if (!W.walkable(nx, nz, cf)) continue;
        const turn = cur.dir !== -1 && cur.dir !== i ? penalty : 0;
        const nd = cur.d + 1 + turn;
        const nk = key(cur.fi, nx, nz, i);
        if (nd < (dist.get(nk) ?? Infinity)) {
          dist.set(nk, nd);
          back.set(nk, cur.k);
          pq.push({ k: nk, d: nd, fi: cur.fi, x: nx, z: nz, dir: i });
        }
      }

      // 竖向跳层（仅当当前格与邻层同位均为RISER，权重FLOOR_HEIGHT + 折弯惩罚）
      // 系统限制：竖井 systemId 必须与当前管线系统一致才能跨层
      if (cf.type[cur.x][cur.z] === T.RISER) {
        const shaft = W.shaftAtCell(cur.x, cur.z);
        const sysOk = !sysLimit || (shaft != null && shaft.systemId === allowSystem);
        if (sysOk) {
          for (const nfi of [cur.fi - 1, cur.fi + 1]) {
            if (nfi < 0 || nfi >= S.floors.length) continue;
            const nf = S.floors[nfi];
            if (nf.type[cur.x][cur.z] !== T.RISER) continue;
            const turn = cur.dir !== -1 && cur.dir !== 4 ? penalty : 0;
            const nd = cur.d + FH + turn;
            const nk = key(nfi, cur.x, cur.z, 4);
            if (nd < (dist.get(nk) ?? Infinity)) {
              dist.set(nk, nd);
              back.set(nk, cur.k);
              pq.push({ k: nk, d: nd, fi: nfi, x: cur.x, z: cur.z, dir: 4 });
            }
          }
        }
      }
    }

    return { path: null, visited: order };
  },

  /**
   * 计算路径真实长度
   * 横步1，竖步FLOOR_HEIGHT
   */
  pathLength(path: Node3D[]): number {
    const C = App.Config;
    let len = 0;
    for (let i = 1; i < path.length; i++) {
      const dv = path[i].fi - path[i - 1].fi;
      len += dv !== 0 ? C.FLOOR_HEIGHT : 1;
    }
    return len;
  },

  /**
   * 计算折点数
   * 3D方向变化（含纵↔横转折）
   */
  countCorners(path: Node3D[]): number {
    if (path.length < 3) return 0;
    let n = 0;
    let pd: [number, number, number] | null = null;
    for (let i = 1; i < path.length; i++) {
      const d: [number, number, number] = [
        path[i].fi - path[i - 1].fi,
        path[i].x - path[i - 1].x,
        path[i].z - path[i - 1].z
      ];
      if (pd && (pd[0] !== d[0] || pd[1] !== d[1] || pd[2] !== d[2])) n++;
      pd = d;
    }
    return n;
  },

  // ════════════════════════════════════════════════════════════════════
  // 绑定（跨楼层）
  // ════════════════════════════════════════════════════════════════════

  /**
   * 获取机房的绑定集合
   */
  boundSetFor(roomId: string): Set<string> {
    for (const f of App.Store.floors) {
      if (f.bindings.has(roomId)) return f.bindings.get(roomId)!;
    }
    return new Set();
  },

  /**
   * 打印所有绑定关系（用于调试）
   */
  dumpBindings(): void {
    console.log('=== 所有绑定关系 ===');
    console.log('绑定模式:', App.Store.bindMode);
    let totalBindings = 0;

    // 统计每个末端被多少个机房绑定
    const endBindCount = new Map<string, number>();

    for (const f of App.Store.floors) {
      console.log(`楼层: ${f.name}`);
      console.log(`  机房数量: ${f.rooms.length}`, f.rooms.map((r: any) => r.id).join(', '));
      console.log(`  末端数量: ${f.ends.length}`, f.ends.map((e: any) => e.id).join(', '));
      console.log(`  绑定数量: ${f.bindings.size}`);
      for (const [rid, set] of f.bindings) {
        console.log(`    ${rid} ->`, Array.from(set));
        totalBindings += set.size;
        // 统计每个末端被绑定的次数
        for (const eid of set) {
          endBindCount.set(eid, (endBindCount.get(eid) || 0) + 1);
        }
      }
    }

    console.log(`总绑定数: ${totalBindings}`);
    console.log('--- 每个末端被绑定次数 ---');
    for (const [eid, count] of endBindCount) {
      console.log(`  ${eid}: ${count} 个机房`);
    }
    console.log('==================');
  },

  /**
   * 获取绑定到指定末端的机房ID列表
   */
  boundRoomIds(endId: string): string[] {
    const ids: string[] = [];
    for (const f of App.Store.floors) {
      for (const [rid, set] of f.bindings) {
        if (set && set.has(endId)) ids.push(rid);
      }
    }
    return ids;
  },

  /**
   * 获取管线系统对象
   */
  systemOf(systemId: number): { id: number; name: string; color: number } | null {
    return App.Store.systems.find(s => s.id === systemId) || null;
  },

  /**
   * 获取管线系统颜色（找不到时回退到默认色）
   */
  systemColorOf(systemId: number, fallback = 0x3da9fc): number {
    const sys = this.systemOf(systemId);
    return sys ? sys.color : fallback;
  },

  /**
   * 获取系统名称
   */
  systemNameOf(systemId: number): string {
    const sys = this.systemOf(systemId);
    return sys ? sys.name : '未分类';
  },

  /**
   * 获取系统简称（水/暖/电），用于名称后备注
   */
  systemAbbrOf(systemId: number): string {
    const sys = this.systemOf(systemId);
    return sys ? (sys.abbr || '') : '';
  },

  /**
   * 获取末端连接的源机房ID列表
   * 规则：每个专业的机房只连接同专业末端（水机房东只连水末端）
   *   - 自动模式：所有同系统机房
   *   - 绑定模式：手动绑定中、且与末端同系统的机房
   */
  sourceRoomIds(end: End): string[] {
    const allRooms = this.allRooms();
    const roomById = new Map(allRooms.map(r => [r.id, r]));

    let ids: string[];
    if (!App.Store.bindMode) {
      ids = allRooms.map(r => r.id);
    } else {
      ids = this.boundRoomIds(end.id);
    }

    // 强制：只保留与末端同系统的机房
    return ids.filter(rid => roomById.get(rid)?.systemId === end.systemId);
  },

  /**
   * 根据机房ID列表获取所有格子节点
   */
  cellsOfRoomIds(ids: string[]): Node3D[] {
    const W = App.World;
    const out: Node3D[] = [];
    const seen = new Set<string>();

    for (let fi = 0; fi < App.Store.floors.length; fi++) {
      const f = App.Store.floors[fi];
      for (const r of f.rooms) {
        if (!ids.includes(r.id)) continue;
        for (const c of W.roomCells(r)) {
          const k = fi + ':' + c.x + ',' + c.z;
          if (!seen.has(k)) {
            seen.add(k);
            out.push({ fi, x: c.x, z: c.z });
          }
        }
      }
    }
    return out;
  },

  // ════════════════════════════════════════════════════════════════════
  // 主流程：运行寻路
  // ════════════════════════════════════════════════════════════════════

  /**
   * 运行寻路主流程
   */
  async run(): Promise<void> {
    const S = App.Store;
    const R = App.Routing;
    const UI = App.UI;
    const RR = App.Render;
    const W = App.World;

    if (S.running) return;
    S.running = true;
    (document.getElementById('btnRun') as HTMLButtonElement).disabled = true;

    RR.gPath.clear();
    RR.clearFlows();
    RR.gSearch.clear();
    S.results = [];

    UI.setProgress('正在生成连接路径（跨楼层）…');
    let tAll = 0;
    const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

    const ends = R.allEnds();

    // 遍历每个末端：取同专业机房（自动模式=所有同系统机房；绑定模式=绑定的同系统机房）
    for (let i = 0; i < ends.length; i++) {
      const e = ends[i];

      const srcIds = R.sourceRoomIds(e);

      if (!srcIds.length) {
        const sysName = R.systemNameOf(e.systemId);
        S.results.push({
          end: e,
          len: 0,
          cor: 0,
          ok: false,
          from: null,
          reason: '未绑定机房'
        });
        UI.setProgress(`× ${e.id} 无同专业（${sysName}）机房可连接`);
        UI.refreshMetrics(tAll);
        await sleep(180);
        continue;
      }

      const efi = e.floorIdx != null
        ? e.floorIdx
        : S.floors.findIndex(f => f.ends.indexOf(e) >= 0);

      // 为每个绑定的机房生成一条路径
      let hasValidPath = false;
      for (let j = 0; j < srcIds.length; j++) {
        const roomId = srcIds[j];

        const t0 = performance.now();

        // 只从当前机房开始寻路（跨层只允许同系统竖井）
        const { path, visited } = R.dijkstra(
          R.cellsOfRoomIds([roomId]),
          { fi: efi < 0 ? 0 : efi, x: e.x, z: e.z },
          e.systemId
        );
        const dt = performance.now() - t0;
        tAll += dt;

        if (path) {
          hasValidPath = true;
          const len = R.pathLength(path);
          const cor = R.countCorners(path);
          const startCell = path[0];
          const sf = S.floors[startCell.fi];
          const fromRoom = sf.rooms.find(r =>
            W.roomCells(r).some(c => c.x === startCell.x && c.z === startCell.z)
          );

          S.results.push({
            end: e,
            len,
            cor,
            ok: true,
            visited,
            from: fromRoom ? fromRoom.id : null
          });

          if (S.results.length === 1) RR.buildSearch(visited);

          const mesh = RR.buildPath(e, path);
          RR.animateGrow(mesh, performance.now(), 320);

          UI.setProgress(
            `✓ ${e.id} <- ${fromRoom ? fromRoom.id : roomId} · 长度 ${len}m · 折点 ${cor}`
          );
        } else {
          S.results.push({
            end: e,
            len: 0,
            cor: 0,
            ok: false,
            from: roomId,
            reason: '隔离'
          });
          UI.setProgress(`× ${e.id} <- ${roomId} 不可达（被禁行区隔离 / 缺竖井跨层）`);
        }

        UI.refreshMetrics(tAll);
        await sleep(260);
      }

      if (!hasValidPath) {
        UI.setProgress(`× ${e.id} 所有绑定机房均不可达`);
      }
    }

    const totalEnds = ends.length;
    const okPaths = S.results.filter(r => r.ok).length;
    const totalLen = S.results.reduce((s, r) => s + r.len, 0);

    UI.setProgress(
      okPaths > 0
        ? `完成 · ${okPaths}条路径连通 · 总长 ${totalLen}m` +
          (S.bindMode ? ' · 手动绑定（每个末端连接所有绑定机房）' : ' · 自动最近机房')
        : (S.bindMode ? '无可连通路径（检查机房↔末端绑定）' : '无可连通路径')
    );

    (document.getElementById('btnRun') as HTMLButtonElement).disabled = false;
    S.running = false;
  },
};

export default App.Routing;
export const Routing = App.Routing;
